import { isTeamUser } from '@upright/common/utils';
import { isNil } from 'lodash';
import { ModelEntityType } from '../../model/input/types';
import { User, UserWithPlainGroups } from '../users/types';
import {
  Organization,
  OrganizationEntityReference,
  OrganizationType,
  SpecialOrganizationId,
} from './organization-types';

export type EntityCountModelEntityType = ModelEntityType.COMPANY | ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;

export type EntityCount = {
  [key in EntityCountModelEntityType]: number;
};

export const getEntityTypeCounts = (entityReferences: OrganizationEntityReference[]) => {
  return {
    [ModelEntityType.COMPANY]: entityReferences.filter(e => e.entityRef.type === ModelEntityType.COMPANY).length,
    [ModelEntityType.GROUP]: entityReferences.filter(e => e.entityRef.type === ModelEntityType.GROUP).length,
    [ModelEntityType.PORTFOLIO]: entityReferences.filter(e => e.entityRef.type === ModelEntityType.PORTFOLIO).length,
  };
};

/**
 * A person belongs to one platform organization, but may also belong to any number of Arvo
 * accounts (`OrganizationType.ARVO`), which only the Arvo console uses (it resolves them itself,
 * `upserve/modules/arvo/account/context.ts`). So the platform's organization of a person is their
 * non-Arvo one whenever they have one; an Arvo organization only for a person with nothing else.
 */
export function getDefaultOrganization(organizations: Organization[] | undefined, isTeam: boolean) {
  if (organizations?.length) {
    if (isTeam) {
      const teamOrg = findTeamOrganization(organizations);
      if (teamOrg) {
        return teamOrg;
      }
    }
    return organizations.find(o => !isArvoOrganization(o)) ?? organizations[0];
  }
  return;
}

export function isArvoOrganization(organization: Pick<Organization, 'type'>): boolean {
  return organization.type === OrganizationType.ARVO;
}

/** Every organization the person belongs to through their groups, each once. */
export function getOrganizationsOfUser(
  user: Pick<User, 'groups'> | Pick<UserWithPlainGroups, 'groups'>,
): Organization[] {
  const seen = new Set<number>();
  return findOrganizationsFromGroups(user.groups).filter(o => {
    if (seen.has(o.id)) return false;
    seen.add(o.id);
    return true;
  });
}

function findTeamOrganization<T extends { id: number }>(input: T[]) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  return input.find(i => i.id === SpecialOrganizationId.UPRIGHT);
}

export function getOrganizationOfUser(
  user: Pick<User, 'groups'> | Pick<UserWithPlainGroups, 'groups'>,
): Organization | undefined {
  const userOrgs = findOrganizationsFromGroups(user.groups);
  const isTeam = isTeamUser(user);
  return getDefaultOrganization(userOrgs, isTeam);
}

export function getOrganizationLinkedCompanyId(user: Pick<User, 'groups'>): string | undefined {
  const organization = getOrganizationOfUser(user);
  if (!organization) {
    return undefined;
  }
  if (!organization.companyId) {
    return undefined;
  }
  return organization.companyId;
}

function findOrganizationsFromGroups<T extends { organization?: Organization; parents?: T[] }>(
  userGroups: T[],
): Organization[] {
  if (userGroups.length === 0) {
    return [];
  }
  const directOrganizations = userGroups.filter(group => !isNil(group.organization)).map(group => group.organization!);
  const parentGroups = getParents(userGroups);
  return [...directOrganizations, ...findOrganizationsFromGroups(parentGroups)];
}

function getParents<T extends { parents?: T[] }>(userGroups: T[]) {
  return userGroups.flatMap(group => group.parents || []);
}
